import { useState, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import {
  getActiveDeliveryMethodsForCase,
  queueManualDelivery,
} from "../services/signOff.service";
import { reportDeliveryRecallQueryKey } from "./useReportDeliveryRecall";

export function useSendReport(caseId: string | undefined) {
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const [modalOpened, { open, close }] = useDisclosure(false);

  const handleClick = useCallback(async () => {
    if (!caseId) return;
    setIsSending(true);
    try {
      const methods = await queryClient.fetchQuery({
        queryKey: ["case-delivery-methods", caseId],
        queryFn: () => getActiveDeliveryMethodsForCase(caseId),
        staleTime: 30_000,
      });

      if (methods.length === 1) {
        // Exactly one method — queue directly without opening modal
        const result = await queueManualDelivery(caseId, [methods[0].id]);
        const count = result.queued?.length ?? 1;
        notifications.show({
          color: "green",
          title: "Report Queued",
          message: `${count} delivery queued via ${methods[0].deliveryMethodName} to ${methods[0].recipientName}`,
        });
        queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
        queryClient.invalidateQueries({
          queryKey: reportDeliveryRecallQueryKey(caseId),
        });
      } else {
        // 0 methods (show no-method UI) or 2+ (show checklist)
        open();
      }
    } catch {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Failed to load delivery methods. Please try again.",
      });
    } finally {
      setIsSending(false);
    }
  }, [caseId, queryClient, open]);

  return { handleClick, isSending, modalOpened, closeModal: close };
}
